import React from 'react';
import { 
  Eye, 
  Heart, 
  Info, 
  CheckCircle2, 
  ArrowRight, 
  Search, 
  Award, 
  AlertCircle,
  Lightbulb,
  PawPrint,
  Smile,
  Shield,
  Star,
  TrendingUp
} from 'lucide-react';

const AnalysisInsightsGrid = ({
  insightsAdicionales = [],
  puntosFuertes = [],
  oportunidadesMejora = []
}) => {
  if (!insightsAdicionales.length && !puntosFuertes.length && !oportunidadesMejora.length) {
    return null;
  }
  
  // Helper function to get appropriate icon for positive points
  const getPositiveIcon = (text) => {
    if (text.toLowerCase().includes('energía') || text.toLowerCase().includes('activ')) 
      return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (text.toLowerCase().includes('pelaje') || text.toLowerCase().includes('pelo')) 
      return <Smile className="h-4 w-4 text-green-500" />;
    if (text.toLowerCase().includes('salud')) 
      return <Shield className="h-4 w-4 text-green-500" />;
    if (text.toLowerCase().includes('social') || text.toLowerCase().includes('amigable')) 
      return <Heart className="h-4 w-4 text-green-500" />;
    if (text.toLowerCase().includes('intelig')) 
      return <Lightbulb className="h-4 w-4 text-green-500" />;
    return <Star className="h-4 w-4 text-green-500" />;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Observaciones del Análisis */}
      {insightsAdicionales.length > 0 && (
        <div className="relative rounded-xl bg-white border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
          {/* Top accent line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-blue-500"></div>
          
          {/* Decorative pattern */}
          <div className="absolute top-0 right-0 opacity-5">
            <PawPrint className="h-24 w-24 text-blue-500 transform rotate-12" />
          </div>
          
          {/* Encabezado */}
          <div className="bg-gradient-to-r from-blue-50 to-blue-50/50 p-5 flex items-center gap-3 border-b border-blue-100">
            <div className="h-9 w-9 rounded-full bg-blue-100 border border-blue-200 shadow-sm flex items-center justify-center">
              <Eye className="h-5 w-5 text-blue-600" />
            </div>
            <h2 className="text-lg font-semibold text-blue-800">
              Observaciones del Análisis
            </h2>
          </div>

          {/* Contenido */}
          <div className="p-5 space-y-4">
            {insightsAdicionales.map((insight, idx) => (
              <div key={idx} className="flex items-start gap-3 group p-2 hover:bg-blue-50/50 rounded-lg transition-colors">
                <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5 border border-blue-200 shadow-sm group-hover:scale-110 transition-transform">
                  <Search className="h-3.5 w-3.5 text-blue-600" />
                </div>
                <p className="text-base text-gray-700 leading-relaxed">{insight}</p>
              </div>
            ))}
          </div>
          
          {/* Footer */}
          <div className="px-5 py-3 bg-gray-50 text-xs text-gray-500 flex items-center justify-center border-t border-gray-100">
            <Info className="h-3 w-3 mr-1" /> Observaciones basadas en análisis visual
          </div>
        </div>
      )}

      {/* Aspectos Destacados */}
      {(puntosFuertes.length > 0 || oportunidadesMejora.length > 0) && (
        <div className="relative rounded-xl bg-white border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
          {/* Top accent line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 to-green-500"></div>
          
          {/* Decorative pattern */}
          <div className="absolute top-0 right-0 opacity-5">
            <PawPrint className="h-24 w-24 text-green-500 transform rotate-12" />
          </div>
          
          {/* Encabezado */}
          <div className="bg-gradient-to-r from-green-50 to-green-50/50 p-5 flex items-center gap-3 border-b border-green-100">
            <div className="h-9 w-9 rounded-full bg-green-100 border border-green-200 shadow-sm flex items-center justify-center">
              <Award className="h-5 w-5 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-green-800">
              Aspectos destacados
            </h2>
          </div>

          {/* Contenido */}
          <div className="p-5 space-y-6">
            {/* Características Positivas */}
            {puntosFuertes.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Características Positivas
                  </h3>
                </div>
                
                <div className="bg-green-50/50 rounded-lg p-3 border border-green-100">
                  {puntosFuertes.map((punto, idx) => (
                    <div key={idx} className="flex items-start gap-3 group p-2 hover:bg-white rounded-lg transition-colors mb-2 last:mb-0">
                      <div className="h-6 w-6 rounded-full bg-white flex items-center justify-center flex-shrink-0 mt-0.5 border border-green-200 shadow-sm group-hover:scale-110 transition-transform">
                        {getPositiveIcon(punto)}
                      </div>
                      <p className="text-base text-gray-700 leading-relaxed">
                        {punto}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Aspectos a cuidar */}
            {oportunidadesMejora.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Aspectos a cuidar
                  </h3>
                </div>
                
                <div className="bg-amber-50/50 rounded-lg p-3 border border-amber-100">
                  {oportunidadesMejora.map((aspecto, idx) => (
                    <div key={idx} className="flex items-start gap-3 group p-2 hover:bg-white rounded-lg transition-colors mb-2 last:mb-0">
                      <div className="h-6 w-6 rounded-full bg-white flex items-center justify-center flex-shrink-0 mt-0.5 border border-amber-200 shadow-sm group-hover:scale-110 transition-transform">
                        <ArrowRight className="h-3.5 w-3.5 text-amber-500" />
                      </div>
                      <p className="text-base text-gray-700 leading-relaxed">
                        {aspecto}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="px-5 py-3 bg-gray-50 text-xs text-gray-500 flex items-center justify-center border-t border-gray-100">
            <Info className="h-3 w-3 mr-1" /> Basado en análisis comparativo con perros de raza similar
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisInsightsGrid;